import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { IncidentHeatMap } from "./IncidentHeatMap";
import { TrendUp, TrendDown, Minus, Warning, Sparkle, Megaphone } from "@phosphor-icons/react";

const KIND_COLOUR = { accident: "#DC2626", near_miss: "#F59E0B", other: "#94A3B8" };

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-popover p-2 text-xs shadow-md">
      <p className="font-semibold mb-1">{label} · {row.total} incident{row.total === 1 ? "" : "s"}</p>
      <p style={{ color: KIND_COLOUR.accident }}>Accidents: {row.accident}</p>
      <p style={{ color: KIND_COLOUR.near_miss }}>Near misses: {row.near_miss}</p>
      {row.other > 0 && <p className="text-muted-foreground">Other: {row.other}</p>}
      {row.injuries > 0 && <p className="text-muted-foreground">{row.injuries} injured</p>}
    </div>
  );
}

function Bars({ rows, max, testid }) {
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {rows.map((c) => (
        <li key={c.key || c.label} data-testid={`${testid}-${c.key || c.label}`}>
          <div className="flex items-center justify-between text-xs"><span className="truncate">{c.label}</span><span className="text-muted-foreground shrink-0 ml-2">{c.total}{c.pct != null ? ` · ${c.pct}%` : ""}</span></div>
          <div className="h-2 rounded-full bg-muted overflow-hidden flex">
            <div style={{ width: `${(100 * (c.accident || 0)) / max}%`, background: KIND_COLOUR.accident }} />
            <div style={{ width: `${(100 * (c.near_miss || 0)) / max}%`, background: KIND_COLOUR.near_miss }} />
            <div style={{ width: `${(100 * (c.other || 0)) / max}%`, background: KIND_COLOUR.other }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Spike banner: the alert + the AI-suggested bulletin on the dominant cause (auto-drafted by the daily tick, or drafted here on request). */
function SpikeBanner({ sp, onDrafted }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const b = sp.bulletin;
  const draft = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/hse/incidents/spikes/bulletin", { month: sp.month, kind: sp.kind });
      toast.success(data.message);
      onDrafted?.();
      nav(`/health-safety/bulletins/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draft the bulletin"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-800 dark:text-red-200 flex flex-wrap items-start gap-2" data-testid={`inc-spike-${sp.kind}-${sp.month}`}>
      <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
      <span className="flex-1 min-w-[240px]"><span className="font-semibold">Spike: {sp.count} {sp.label.toLowerCase()} in {sp.month_label}</span>{sp.multiple ? ` — ${sp.multiple}× the 12-month monthly average of ${sp.average}` : ` — the 12-month monthly average is ${sp.average}`}. {sp.kind === "near_miss" ? "Check the causes and sites below before it turns into an accident trend." : "Review the causes and consider a safety bulletin or toolbox talk."} The H&amp;S Manager has been alerted.</span>
      {b ? (
        <Link to={`/health-safety/bulletins/${b.id}`} className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-background px-2 py-1 font-medium text-red-800 dark:text-red-200 hover:border-primary/60 shrink-0" data-testid={`inc-spike-bulletin-${sp.kind}-${sp.month}`}>
          <Megaphone size={12} /> {b.status === "draft" ? "Review suggested bulletin" : "Bulletin sent"} · {b.reference}{b.cause_label ? ` · ${b.cause_label}` : ""}{b.ai_state === "pending" ? " · drafting…" : ""}
        </Link>
      ) : (
        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" disabled={busy} onClick={draft} data-testid={`inc-spike-draft-${sp.kind}-${sp.month}`}><Sparkle size={12} className="mr-1" /> {busy ? "Drafting…" : "Draft bulletin with AI"}</Button>
      )}
    </div>
  );
}

/** 12-month pattern spotting: accidents vs near misses per month, top causes, top sites and a site × cause heat map. */
export function IncidentTrends() {
  const [t, setT] = useState(null);
  const load = useCallback(() => API.get("/hse/incidents/trends").then((r) => setT(r.data)).catch(() => setT({ months: [], causes: [], sites: [], totals: {}, trend: {} })), []);
  useEffect(() => { load(); }, [load]);
  if (!t) return null;
  const tot = t.totals || {};
  const tr = t.trend || {};
  const TrendIcon = tr.delta_pct == null ? Minus : tr.delta_pct > 0 ? TrendUp : TrendDown;
  const trendTone = tr.delta_pct == null ? "text-muted-foreground" : tr.delta_pct > 0 ? "text-red-600" : "text-emerald-600";
  const maxCause = Math.max(1, ...(t.causes || []).map((c) => c.total));
  const maxSite = Math.max(1, ...(t.sites || []).map((c) => c.total));
  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="inc-trends">
      {(t.spikes || []).length > 0 && (
        <div className="mb-3 space-y-1.5">
          {t.spikes.map((sp) => <SpikeBanner key={`${sp.month}-${sp.kind}`} sp={sp} onDrafted={load} />)}
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <h2 className="text-sm font-semibold">12-month trends</h2>
          <p className="text-xs text-muted-foreground">{tot.incidents ?? 0} incident{tot.incidents === 1 ? "" : "s"} · {tot.accidents ?? 0} accident{tot.accidents === 1 ? "" : "s"} · {tot.near_misses ?? 0} near miss{tot.near_misses === 1 ? "" : "es"} · {tot.injuries ?? 0} injured · {tot.lost_days ?? 0} lost day{tot.lost_days === 1 ? "" : "s"} · {tot.riddor ?? 0} RIDDOR</p>
        </div>
        <div className={`text-xs inline-flex items-center gap-1 ${trendTone}`} data-testid="inc-trends-delta"><TrendIcon size={14} weight="bold" /> {tr.delta_pct == null ? `${tr.recent ?? 0} in the last ${tr.months_each ?? 6} months` : `${tr.delta_pct > 0 ? "+" : ""}${tr.delta_pct}% vs the previous ${tr.months_each} months`}{t.near_miss_ratio != null && <span className="text-muted-foreground"> · {t.near_miss_ratio} near misses per accident</span>}</div>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-56" data-testid="inc-trends-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={t.months} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} className="fill-muted-foreground" width={28} />
              <Tooltip content={<Tip />} cursor={{ fill: "var(--muted)", opacity: 0.4 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="accident" stackId="a" name="Accidents" fill={KIND_COLOUR.accident} />
              <Bar dataKey="near_miss" stackId="a" name="Near misses" fill={KIND_COLOUR.near_miss} />
              <Bar dataKey="other" stackId="a" name="Other" fill={KIND_COLOUR.other} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-4">
          <div>
            <h3 className="text-xs font-semibold mb-1.5">Top causes</h3>
            {t.causes?.length ? <Bars rows={t.causes} max={maxCause} testid="inc-trends-cause" /> : <p className="text-xs text-muted-foreground">Causes show here once incidents are logged with a cause.</p>}
          </div>
          <div>
            <h3 className="text-xs font-semibold mb-1.5">Where it happens</h3>
            {t.sites?.length ? <Bars rows={t.sites.map((s) => ({ ...s, key: s.label }))} max={maxSite} testid="inc-trends-site" /> : <p className="text-xs text-muted-foreground">No incidents in the last 12 months.</p>}
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <h3 className="text-xs font-semibold mb-2">Heat map — sites × causes</h3>
        <IncidentHeatMap heatmap={t.heatmap} />
      </div>
    </section>
  );
}
